import { useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  ALERT_SEVERITY_LABEL,
  ALERT_STATUS_LABEL,
  RANGE_LABEL,
  type AlertHistoryMeta,
  type AlertInstance,
  type AlertSeverity,
} from "@k8s-dashboard/contracts";
import { alertKeys, useAlerts } from "@/api/queries";
import { useDashboardParams } from "@/state/useDashboardParams";
import { Panel, StatusBadge, StatusDot } from "@/components/primitives";
import { SectionView } from "@/components/SectionState";
import { Breadcrumb, LogsLink, currentRouteCrumb, refPath, routeCrumbAt } from "@/components/drill";
import { dayClock, duration, num, since } from "@/lib/format";
import { PageError, PageHeader, useDrillControls, useInvalidate } from "@/features/drill/common";
import { useT, type Translate } from "@/i18n/I18nProvider";

const SEV_TO_STATUS = { critical: "critical", warning: "warning", info: "progressing" } as const;

export function alertSectionCount(section: { status: string; data?: unknown[] } | undefined) {
  return section?.status === "ok" ? num(section.data?.length ?? 0) : "—";
}

/**
 * 해소 건수는 **조회가 창 전체를 덮었을 때만** 정확한 값입니다(ADR 0049 결정 5).
 * 절단·drop이 있거나 조회가 실패했으면 `—`로 둡니다 — `N+` 같은 하한 표기를 쓰지
 * 않습니다. 하한은 사용자가 "적어도 이만큼"으로 읽고 총계처럼 다루게 됩니다.
 */
export function resolvedCountLabel(count: number | undefined, history: AlertHistoryMeta | undefined) {
  if (count === undefined) return "—";
  if (!history?.resolvedCountsAvailable) return "—";
  if (history.queryCoverage !== "complete") return "—";
  return num(count);
}

/** 발화 건수는 Alertmanager 조회가 성공했을 때만 값이 있습니다. 부재는 0이 아닙니다. */
export function firingCountLabel(count: number | undefined) {
  return count === undefined ? "—" : num(count);
}

/**
 * 시작 시각을 모르면 지속시간을 만들지 않습니다. `Date.parse(undefined)`는 NaN이고
 * 그 NaN이 화면에 "NaN분"으로 나가는 것이 이 가드가 막는 실제 증상입니다.
 */
export function alertElapsedLabel(alert: AlertInstance, referenceIso: string | undefined) {
  if (alert.status !== "firing") {
    return alert.endsAt && referenceIso ? since(alert.endsAt, referenceIso) : "—";
  }
  if (!alert.startsAt || !referenceIso) return "—";
  return duration((Date.parse(referenceIso) - Date.parse(alert.startsAt)) / 1000);
}

export function alertStartLabel(alert: AlertInstance, referenceIso: string | undefined) {
  if (!alert.startsAt || !referenceIso) return "시작 시각 미확인";
  return since(alert.startsAt, referenceIso);
}

/**
 * 절단 배너의 문구입니다. 모든 문장은 **ko/en 카탈로그**에서 옵니다 — 컴포넌트에
 * 한국어를 하드코딩하면 English 모드가 조용히 한국어를 읽습니다.
 * "더 보기"는 만들지 않습니다 — 안정적인 다음 페이지가 없습니다.
 */
export function historyBannerText(history: AlertHistoryMeta | undefined, t: Translate) {
  if (!history || !history.truncated) return null;
  const key =
    history.truncationReason === "upstream_raw_cap"
      ? "common.alerts.history.truncated.rawCap"
      : history.truncationReason === "display_cap"
        ? "common.alerts.history.truncated.displayCap"
        : "common.alerts.history.truncated.generic";
  const summary = t("common.alerts.history.truncated.summary", {
    returned: num(history.returned),
    accepted: num(history.accepted),
    rawReturned: num(history.rawReturned),
    coverage: history.queryCoverage,
  });
  return `${t(key)} ${summary}`;
}

/**
 * 이력 섹션이 degraded일 때의 문구입니다. 사유 코드별로 구분하고, 이미 있는
 * `common.state.degraded.*` 키를 **재사용**합니다(신규 0 B).
 */
export function historyDegradedText(
  section: { status: string; reason?: string; source?: string } | undefined,
  t: Translate,
) {
  if (!section || section.status !== "degraded") return null;
  switch (section.reason) {
    case "history_not_configured":
      return t("common.state.degraded.history.detail");
    case "history_scope_unsupported":
      return t("common.alerts.history.scopeUnsupported.detail");
    case "history_scope_too_large":
      return t("common.state.degraded.historyScope.detail");
    case "alerts_not_configured":
      return t("common.state.degraded.alerts.detail");
    case "multiple_upstreams_unavailable":
      return t("common.alerts.history.bothUnavailable");
  }
  const source =
    section.source === "grafana" ? "Grafana" : section.source === "alertmanager" ? "Alertmanager" : undefined;
  const title = source
    ? t("common.state.degraded.titleWithSource", { source })
    : t("common.alerts.source.unknown");
  return `${title} — ${t("common.state.degraded.detail")}`;
}

/** degraded 섹션의 제목입니다. 사유가 있는 경우에만 전용 제목을 씁니다. */
export function historyDegradedTitle(
  section: { status: string; reason?: string } | undefined,
  t: Translate,
) {
  if (!section || section.status !== "degraded") return null;
  switch (section.reason) {
    case "history_not_configured":
      return t("common.state.degraded.history.title");
    case "history_scope_unsupported":
      return t("common.alerts.history.scopeUnsupported.title");
    case "history_scope_too_large":
      return t("common.state.degraded.historyScope.title");
    case "alerts_not_configured":
      return t("common.state.degraded.alerts.title");
  }
  return t("common.state.degraded.title");
}

export function AlertsView() {
  const { search } = useLocation();
  const [params, setParams] = useSearchParams();
  const { clusterId, namespace, range, from, to, refreshMs } = useDashboardParams();
  const { t } = useT("common");
  const q = useAlerts(clusterId, namespace, range, refreshMs, from, to);
  const invalidate = useInvalidate(alertKeys.list(clusterId, namespace, range));
  const { controls } = useDrillControls(invalidate, q.isFetching, q.dataUpdatedAt || undefined);

  const tab = (params.get("tab") === "resolved" ? "resolved" : "firing") as "firing" | "resolved";
  const setTab = (next: string) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", next);
        p.delete("alert");
        return p;
      },
      { replace: true },
    );

  const section = tab === "firing" ? q.data?.firing : q.data?.resolved;
  const list = section?.data ?? [];
  const selectedId = params.get("alert") ?? list[0]?.id ?? null;
  const selected = list.find((a) => a.id === selectedId) ?? null;
  const ref = q.data?.generatedAt;
  /* 이력 메타는 Section이 아니라 평문 필드입니다 — 조회가 실제로 일어났을 때만 있습니다.
     배너는 **탭과 무관하게** 보이고 `counts.data`의 nil 여부에 의존하지 않습니다
     (ADR 0050 D7) — 배너를 띄우려고 살아 있는 건수를 지우는 구조를 만들지 않습니다. */
  const history = q.data?.history;
  const truncationBanner = historyBannerText(history, t);
  /* 사유를 든 섹션을 고릅니다. 이력 쪽이 degraded면 그쪽이 가장 구체적이고, 이력이
     멀쩡하면 `counts`가 D7 표의 단일 귀속 사유를 들고 있습니다(발화 원천 미구성·장애).
     둘 다 `counts.data`와 무관하게 판정합니다. */
  const noticeSection =
    q.data?.resolved?.status === "degraded" ? q.data?.resolved : q.data?.counts;
  const degradedBanner = historyDegradedText(noticeSection, t);
  const degradedTitle = historyDegradedTitle(noticeSection, t);

  return (
    <div className="page">
      <PageHeader
        crumbs={
          <Breadcrumb
            items={[routeCrumbAt("overview", "/", search), currentRouteCrumb("alerts")]}
          />
        }
        title="Alerts"
        subtitle={`${clusterId} · ${namespace === "all" ? "모든 Namespace" : namespace} · ${RANGE_LABEL[range]} · Grafana Alerting / Alertmanager 조회`}
        controls={controls}
      />

      {q.isError ? (
        <PageError error={q.error} onRetry={invalidate} />
      ) : (
        <>
          <div className="notice">
            <span aria-hidden="true">i</span>
            <span>
              이 화면은 <strong>조회 전용</strong>입니다. Rule 편집 · Silence · Notification Routing 변경은 MVP
              비목표이며 원본 시스템에서 수행합니다.
            </span>
          </div>

          <Panel
            title="심각도별 현황"
            subtitle={q.data ? `Grouping 기준 — ${q.data.groupingRule}` : undefined}
            section={q.data?.counts}
            referenceIso={ref}
          >
            <SectionView section={q.data?.counts} loading={q.isLoading} emptyTitle="집계가 없습니다">
              {(c) => (
                <div className="severity-bar">
                  {(Object.keys(c) as AlertSeverity[]).map((sev) => (
                    <span key={sev} className="severity-chip">
                      <StatusDot severity={SEV_TO_STATUS[sev]} />
                      <span className="severity-chip__count">{firingCountLabel(c[sev]?.firing)}</span>
                      <span className="severity-chip__label">
                        {ALERT_SEVERITY_LABEL[sev]}{" "}
                        <span className="muted">· 해소 {resolvedCountLabel(c[sev]?.resolved, history)}</span>
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </SectionView>
          </Panel>

          {degradedBanner && (
            <div className="notice notice--warning" role="status" aria-live="polite" data-testid="alert-history-notice">
              <span aria-hidden="true">▲</span>
              <span>
                {degradedTitle && <strong>{degradedTitle} </strong>}
                {degradedBanner}
              </span>
            </div>
          )}
          {truncationBanner && (
            <div
              className="notice notice--warning"
              role="status"
              aria-live="polite"
              data-testid="alert-history-truncated"
            >
              <span aria-hidden="true">!</span>
              <span>{truncationBanner}</span>
            </div>
          )}

          <div className="grid grid--split">
            <Panel
              title={
                <span className="chips" role="tablist" aria-label="알림 상태">
                  {/* role="tab"에는 aria-pressed가 허용되지 않습니다 — 선택 상태는
                      aria-selected 하나가 말합니다(axe aria-allowed-attr). (#13) */}
                  <button type="button" className="chip" role="tab" aria-selected={tab === "firing"} onClick={() => setTab("firing")}>
                    진행 중
                    <span className="chip__count num">{alertSectionCount(q.data?.firing)}</span>
                  </button>
                  <button type="button" className="chip" role="tab" aria-selected={tab === "resolved"} onClick={() => setTab("resolved")}>
                    해소됨
                    <span className="chip__count num">{alertSectionCount(q.data?.resolved)}</span>
                  </button>
                </span>
              }
              subtitle={tab === "firing" ? "지금 발생 중인 알림" : `${RANGE_LABEL[range]} 안에 해소된 알림`}
              section={section}
              referenceIso={ref}
              flush
            >
              <SectionView
                section={section}
                loading={q.isLoading}
                emptyTitle={tab === "firing" ? "진행 중인 알림이 없습니다" : "해소된 알림이 없습니다"}
                emptyDetail={tab === "firing" ? "현재 Scope에서 발생 중인 알림이 없습니다." : "선택한 범위에 해소된 알림이 없습니다."}
              >
                {(items) => (
                  <div className="panel__scroll" style={{ maxHeight: 520 }}>
                    <table className="ds-data-table ds-data-table--compact ds-data-table--interactive">
                      <thead>
                        <tr>
                          <th>알림</th>
                          <th style={{ width: 150 }}>대상</th>
                          <th style={{ width: 96 }}>시작</th>
                          <th style={{ width: 96 }}>{tab === "firing" ? "지속" : "해소"}</th>
                          <th style={{ width: 70 }}>그룹</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((a) => (
                          <tr
                            key={a.id}
                            tabIndex={0}
                            aria-selected={a.id === selectedId}
                            onClick={() =>
                              setParams(
                                (prev) => {
                                  const p = new URLSearchParams(prev);
                                  p.set("alert", a.id);
                                  return p;
                                },
                                { replace: true },
                              )
                            }
                            onKeyDown={(ev) => {
                              if (ev.key !== "Enter") return;
                              setParams(
                                (prev) => {
                                  const p = new URLSearchParams(prev);
                                  p.set("alert", a.id);
                                  return p;
                                },
                                { replace: true },
                              );
                            }}
                          >
                            <td>
                              <StatusBadge severity={SEV_TO_STATUS[a.severity]} label={a.name} small />
                            </td>
                            <td className="ds-ident">{a.entityName ?? "—"}</td>
                            <td className="num muted">{alertStartLabel(a, ref)}</td>
                            <td className="num muted">{alertElapsedLabel(a, ref)}</td>
                            <td className="ds-num">{a.groupSize > 1 ? `${a.groupSize}건` : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionView>
            </Panel>

            <AlertDetail alert={selected} referenceIso={ref} search={search} groupingRule={q.data?.groupingRule} />
          </div>
        </>
      )}
    </div>
  );
}
/**
 * 원천 이름은 제품 이름입니다. 해소 이력은 Grafana annotations에서 오고 진행 중
 * 알림은 Alertmanager/Grafana Alerting에서 옵니다 — 두 출처를 한 이름으로 덮지 않습니다.
 */
export function sourceLabel(alert: Pick<AlertInstance, "source" | "status">) {
  if (alert.source !== "grafana") return "Alertmanager";
  return alert.status === "resolved" ? "Grafana 해소 이력" : "Grafana Alerting";
}

function AlertDetail({
  alert,
  referenceIso,
  search,
  groupingRule,
}: {
  alert: AlertInstance | null;
  referenceIso?: string;
  search: string;
  groupingRule?: string;
}) {
  const [showLabels, setShowLabels] = useState(false);

  if (!alert) {
    return (
      <Panel title="알림 상세">
        <div className="state">
          <span className="state__glyph" aria-hidden="true">
            →
          </span>
          <span className="state__title">목록에서 알림을 선택하세요</span>
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      title={
        <span className="row">
          {alert.name}
          <StatusBadge severity={SEV_TO_STATUS[alert.severity]} label={ALERT_STATUS_LABEL[alert.status]} small />
        </span>
      }
      subtitle={`${sourceLabel(alert)} · ${alert.severity}`}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
        <div className="facts">
          <div className="fact">
            <span className="fact__label">시작</span>
            <span className="fact__value num">
              {alert.startsAt ? dayClock(Date.parse(alert.startsAt)) : "시작 시각 미확인"}
            </span>
          </div>
          <div className="fact">
            <span className="fact__label">{alert.status === "firing" ? "지속" : "해소"}</span>
            <span className="fact__value num">
              {alert.status === "firing"
                ? alertElapsedLabel(alert, referenceIso)
                : alert.endsAt
                  ? dayClock(Date.parse(alert.endsAt))
                  : "—"}
            </span>
          </div>
          <div className="fact">
            <span className="fact__label">대상</span>
            <span className="fact__value ds-ident">{alert.entityName ?? "매핑 없음"}</span>
          </div>
          <div className="fact">
            <span className="fact__label">묶인 인스턴스</span>
            <span className="fact__value num">{alert.groupSize}건</span>
          </div>
        </div>

        {alert.annotations.summary && (
          <div>
            <div style={{ font: "var(--type-body-strong)" }}>{alert.annotations.summary}</div>
            {alert.annotations.description && (
              <div className="muted" style={{ marginTop: "var(--space-2)" }}>
                {alert.annotations.description}
              </div>
            )}
          </div>
        )}

        {/* Metric → Log → Event 동선. Scope와 시간 범위를 그대로 넘깁니다. */}
        <div className="row row--wrap">
          {alert.entity ? (
            <>
              <Link to={refPath(alert.entity, search)}>관련 대상 상세 →</Link>
              {alert.entity.workloadName && alert.entity.workloadKind && (
                <Link
                  to={refPath(
                    {
                      namespace: alert.entity.namespace,
                      workloadKind: alert.entity.workloadKind,
                      workloadName: alert.entity.workloadName,
                    },
                    search,
                  )}
                >
                  관련 Workload 상세 →
                </Link>
              )}
              <LogsLink resourceRef={alert.entity} search={search}>관련 로그 →</LogsLink>
            </>
          ) : (
            <span className="muted" style={{ font: "var(--type-meta)" }}>
              label에서 Entity를 특정하지 못했습니다. 원본 시스템에서 확인하세요.
            </span>
          )}
          {alert.sourceUrl && (
            <a href={alert.sourceUrl} target="_blank" rel="noopener noreferrer">
              {sourceLabel(alert)} 원본 열기 ↗
            </a>
          )}
        </div>

        <div>
          <button type="button" className="linkish" aria-expanded={showLabels} onClick={() => setShowLabels((v) => !v)}>
            {showLabels ? "라벨 접기" : `라벨 ${Object.keys(alert.labels).length}개 보기`}
          </button>
          {showLabels && (
            <dl className="logline__fields" style={{ marginTop: "var(--space-4)" }}>
              {Object.entries(alert.labels).map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd className="ds-ident">{v}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {alert.groupSize > 1 && groupingRule && (
          <div className="notice notice--warning">
            <span aria-hidden="true">!</span>
            <span>
              같은 규칙의 인스턴스 {alert.groupSize}건이 한 줄로 묶였습니다. 기준: {alert.groupKey}
            </span>
          </div>
        )}
      </div>
    </Panel>
  );
}
